import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { name: t('nav.services'), path: "/leistungen" },
    { name: t('nav.kmuFulfillment'), path: "/kmu-fulfillment" },
    { name: t('nav.about'), path: "/unternehmen" },
    { name: t('nav.blog'), path: "/blog" },
    { name: t('nav.contact'), path: "/kontakt" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Bar - Dark */}
      <div className="bg-slate-800 text-white py-2 text-sm">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="text-slate-400 hidden sm:inline">
              {language === 'de' ? 'Kontaktieren Sie uns!' : 'Contact us!'}
            </span>
            <a href="tel:060284062310" className="flex items-center gap-2 hover:text-orange-500 transition-colors">
              <Phone size={14} aria-hidden="true" />
              <span>06028 / 4062310</span>
            </a>
            <a href="mailto:hallo@kauzlogistik.de" className="flex items-center gap-2 hover:text-orange-500 transition-colors">
              <Mail size={14} aria-hidden="true" />
              <span className="hidden md:inline">hallo@kauzlogistik.de</span>
            </a>
          </div>
          
          {/* Language Switcher */}
          <div className="flex items-center gap-2">
            <Globe size={14} className="text-slate-400" aria-hidden="true" />
            <button
              onClick={() => setLanguage('de')}
              className={`px-2 py-1 text-xs font-medium transition-colors ${
                language === 'de' 
                  ? 'text-orange-500' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="lang-de"
            >
              DE
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={() => setLanguage('en')}
              className={`px-2 py-1 text-xs font-medium transition-colors ${
                language === 'en' 
                  ? 'text-orange-500' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation - White background */}
      <nav className="bg-white sticky top-0 z-50 shadow-sm" data-testid="main-navbar">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-32">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_09913581-f85f-4d5b-af7c-7207d8b9779f/artifacts/akxca88t_Logo_neu.png" 
                alt="Kauz Logistik Logo"
                className="h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors text-slate-700 hover:text-orange-600 ${
                    isActive(link.path) ? "text-orange-600" : ""
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/angebot">
                <Button 
                  className="bg-orange-600 text-white hover:bg-orange-700 uppercase tracking-wider font-bold px-6 transition-all"
                  data-testid="nav-cta-button"
                >
                  {t('nav.requestQuote')}
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-900"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? (
                <X size={28} aria-hidden="true" />
              ) : (
                <Menu size={28} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg transform transition-all duration-300 ${
            isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          data-testid="mobile-menu"
        >
          <div className="container mx-auto px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 font-medium border-b border-slate-100 text-slate-700 hover:text-orange-600 ${
                  isActive(link.path) ? "text-orange-600" : ""
                }`}
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            <Link 
              to="/angebot" 
              className="block mt-4"
              onClick={() => setIsOpen(false)}
            >
              <Button 
                className="w-full bg-orange-600 text-white hover:bg-orange-700 uppercase tracking-wider font-bold"
                data-testid="mobile-cta-button"
              >
                {t('nav.requestQuote')}
              </Button>
            </Link>
            
            {/* Mobile Language Switcher */}
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-4">
              <Globe size={16} className="text-slate-400" aria-hidden="true" />
              <button
                onClick={() => { setLanguage('de'); setIsOpen(false); }}
                className={`px-3 py-1 text-sm font-medium ${
                  language === 'de' 
                    ? 'bg-orange-100 text-orange-600' 
                    : 'text-slate-600'
                }`}
              >
                Deutsch
              </button>
              <button
                onClick={() => { setLanguage('en'); setIsOpen(false); }}
                className={`px-3 py-1 text-sm font-medium ${
                  language === 'en' 
                    ? 'bg-orange-100 text-orange-600' 
                    : 'text-slate-600'
                }`}
              >
                English
              </button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
              <a href="tel:060284062310" className="flex items-center gap-2 py-2 text-slate-600 hover:text-orange-600">
                <Phone size={16} aria-hidden="true" />
                06028 / 4062310
              </a>
              <a href="mailto:hallo@kauzlogistik.de" className="flex items-center gap-2 py-2 text-slate-600 hover:text-orange-600">
                <Mail size={16} aria-hidden="true" />
                hallo@kauzlogistik.de
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
